import React from 'react';
import { Box, Chip } from '@mui/material';
import { CONTENT_TYPES } from './ButtonContent.constants';
import { customChipStyles } from './ButtonContent.constants';
import { ContentType, StateType } from './ButtonContent';
import { updateStateBasedProp } from './ButtonContent.utils';

interface ContentChipsProps {
  currentContentOrder: ContentType[];
  activeContent: ContentType | null;
  setActiveContent: (type: ContentType | null) => void;
  updateImage: (updates: any) => void;
  rawContentOrder: ContentType[] | { default: ContentType[]; hover?: ContentType[] } | undefined;
  activeState: StateType;

  color?: string;
  backgroundColor?: string;
  selectedTabBackgroundColor?: string;
}

const ContentChips: React.FC<ContentChipsProps> = ({
  currentContentOrder,
  activeContent,
  setActiveContent,
  updateImage,
  rawContentOrder,
  activeState,
  color,
  backgroundColor,
  selectedTabBackgroundColor,
}) => {
  const addContent = (type: ContentType) => {
    if (!currentContentOrder.includes(type)) {
      const newContentOrder = [...currentContentOrder, type];
      const updatedContentOrder = updateStateBasedProp(
        rawContentOrder,
        newContentOrder,
        activeState,
        undefined,
        true,
      );
      updateImage({ contentOrder: updatedContentOrder });
    }
    setActiveContent(type);
  };

  const removeContent = (type: ContentType) => {
    const newContentOrder = currentContentOrder.filter((t) => t !== type);
    const updatedContentOrder = updateStateBasedProp(
      rawContentOrder,
      newContentOrder,
      activeState,
      undefined,
      true,
    );
    updateImage({ contentOrder: updatedContentOrder });
    setActiveContent(newContentOrder.length > 0 ? newContentOrder[0] : null);
  };

  return (
    <Box
      sx={{
        p: 2,
        borderTop: '1px solid lightgray',
        backgroundColor,
        marginBottom: '0px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          '& .MuiChip-root': {
            flex: '1 1 calc(50% - 6px)',
            minWidth: 100,
            borderRadius: 2,
            fontWeight: 500,
            position: 'relative',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
          },
        }}
      >
        {CONTENT_TYPES.map(({ type, icon: IconComponent, label }) => {
          const isActive = currentContentOrder.includes(type);

          return (
            <Chip
              key={type}
              icon={<IconComponent />}
              label={label}
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => addContent(type)}
              onDelete={isActive ? () => removeContent(type) : undefined}
              sx={{
                color: isActive ? color : color,
                backgroundColor: isActive ? selectedTabBackgroundColor : 'transparent',
                border: `1.5px solid ${
                  isActive ? selectedTabBackgroundColor : 'rgba(224, 224, 224, 0.6)'
                }`,
                '&:hover': {
                  backgroundColor: isActive
                    ? selectedTabBackgroundColor
                    : 'rgba(224, 224, 224, 0.2)',
                  boxShadow: `0 0 4px ${selectedTabBackgroundColor}`,
                },
                '& .MuiChip-icon': {
                  order: 1,
                  position: 'absolute',
                  left: '8px',
                  margin: 0,
                  opacity: 0.75,
                  color: color,
                },
                '& .MuiChip-label': {
                  order: 2,
                  width: '100%',
                  textAlign: 'center',
                  padding: 0,
                  paddingLeft: '24px',
                  paddingRight: '24px',
                },
                '& .MuiChip-deleteIcon': {
                  order: 3,
                  position: 'absolute',
                  right: '8px',
                  opacity: 0.6,
                  fontSize: '16px',
                  margin: 0,
                  color: color,
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ContentChips;
